import type { MutationResult } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';

import { useErrorContext } from '@xing-com/crate-entity-pages-common';

import type { NetworkContactRequestsMutation } from '../graphql/queries/network-contact-request.gql-types';
import { NetworkContactRequestsDocument } from '../graphql/queries/network-contact-request.gql-types';

type UseNetworkRequestMutationProps = {
  update?: any;
  onCompleted?: () => void;
};

type UseNetworkRequestMutationResponse = [
  (id: string, hasPhoto?: boolean) => void,
  MutationResult<NetworkContactRequestsMutation>,
];

export const useNetworkRequestMutation = ({
  update = () => undefined,
  onCompleted = () => undefined,
}: UseNetworkRequestMutationProps = {}): UseNetworkRequestMutationResponse => {
  const { $t } = useIntl();
  const { showError } = useErrorContext();

  const [executeNetworkRequest, response] =
    useMutation<NetworkContactRequestsMutation>(
      NetworkContactRequestsDocument,
      {
        optimisticResponse: {
          __typename: 'Mutation',
          networkContactRequestsSend: {
            error: null,
          },
        },
        onError: (error) => {
          showError({
            message: 'EP_EMPLOYEES_FEEDBACK_ERROR',
            error,
          });
        },
        onCompleted: (response) => {
          const error = response?.networkContactRequestsSend?.error;
          if (error) {
            showError({
              message: $t({
                id: error.message ?? '',
                defaultMessage: error.message ?? '',
              }),
              error: new Error(error.message ?? ''),
            });
          } else {
            onCompleted();
          }
        },
      }
    );

  const executeNetworkRequestById = (id: string, hasPhoto?: boolean) => {
    executeNetworkRequest({
      variables: {
        input: {
          recipientId: id,
        },
      },
      update: (data, cache) => update(data, cache, id, hasPhoto),
    });
  };

  return [executeNetworkRequestById, response];
};
